import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { ACTIONS } from 'constants/index';
import useGetMeDetails from 'features/AppCreator/hooks/useGetMeDetails';

/**
 * Combined User Context
 * Manages both user authentication state and meDetails
 * - user: Authentication and basic user info
 * - meDetails: Detailed user information from /me endpoint
 */
const LoggedInUserStateContext = createContext();
const LoggedInUserDispatchContext = createContext();

const initialState = { user: null, meDetails: null };
const loggedInUserReducer = (state, action) => {
	switch (action.type) {
		case ACTIONS.LOGGED_IN_USER_LOADED:
			return { ...state, user: action.payload };
		case ACTIONS.ME_DETAILS_LOADED:
			return { ...state, meDetails: action.payload };
		default:
			return state;
	}
};

const LoggedInUserProvider = (props) => {
	const [state, dispatch] = useReducer(loggedInUserReducer, initialState);
	return (
		<LoggedInUserStateContext.Provider value={{ state }}>
			<LoggedInUserDispatchContext.Provider value={{ dispatch }}>{props.children}</LoggedInUserDispatchContext.Provider>
		</LoggedInUserStateContext.Provider>
	);
};

const useLoggedInUserStateContext = () => {
	const { state } = useContext(LoggedInUserStateContext);

	return state;
};
const useLoggedInUserDispatchContext = () => {
	const { dispatch } = useContext(LoggedInUserDispatchContext);

	return dispatch;
};

/**
 * Custom hook to fetch and set meDetails in the combined user context
 * This prevents duplicate API calls across components
 */
const useSetMeDetails = () => {
	const dispatch = useLoggedInUserDispatchContext();
	const state = useLoggedInUserStateContext();
	const { data: meDetails, isLoading, error, isSuccess } = useGetMeDetails();

	useEffect(() => {
		// Only dispatch if we have new data and it's different from current state
		if (isSuccess && meDetails && meDetails !== state.meDetails) {
			dispatch({
				type: ACTIONS.ME_DETAILS_LOADED,
				payload: meDetails
			});
		}
	}, [meDetails, isSuccess, dispatch, state.meDetails]);

	return {
		meDetails: state.meDetails || meDetails,
		isLoading,
		error,
		isSuccess
	};
};

/**
 * Custom hook to access meDetails from the combined user context
 * This allows components to get meDetails without making additional API calls
 */
const useMeDetailsFromContext = () => {
	const state = useLoggedInUserStateContext();
	
	return {
		meDetails: state?.meDetails,
		hasEntities: state?.meDetails?.hasEntities,
		userId: state?.meDetails?.smaUserId || state?.meDetails?.id,
		// Add other commonly used meDetails properties here
		isLoading: !state?.meDetails, // Simple loading state - true if no meDetails yet
		isLoaded: !!state?.meDetails
	};
};

export { LoggedInUserProvider, useLoggedInUserStateContext, useLoggedInUserDispatchContext, useSetMeDetails, useMeDetailsFromContext, loggedInUserReducer };
